import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import './AnalyticsSection.css';

function AnalyticsSection({ areaWiseData }) {
  const [expandedSections, setExpandedSections] = useState({});

  const analytics = useMemo(() => {
    if (!areaWiseData || areaWiseData.length === 0) {
      return {
        lowestQtyPlazas: [],
        lowestQtyAreas: [],
        lowestAmtPlazas: [],
        lowestAmtAreas: [],
        highestOverduePlazas: [],
        highestOverdueAreas: [],
      };
    }

    // List of plazas to exclude
    const excludedPlazas = [
      'Walton Plaza-Computer City, Chittagong',
      'Walton Plaza-Multiplan Center, Dhaka',
      'Walton Plaza-Jalil Tower, Khulna',
      'Walton Plaza-IDB'
    ];

    const plazas = [];
    const areas = new Map();

    // Collect plaza data (excluding specific plazas)
    areaWiseData.forEach(row => {
      if (row.isSubtotal || row.isGrandTotal) return;

      if (row.Plaza && !excludedPlazas.includes(row.Plaza)) {
        plazas.push({
          division: row.Division,
          area: row.Area,
          plaza: row.Plaza,
          collectionQtyPercent: parseFloat(row.Collection_Qty_Percent),
          collectionAmtPercent: parseFloat(row.Collection_Amt_Percent),
          overdueChange: parseFloat(row.Overdue_Change),
        });
      }

      // Collect area data
      if (row.Area && !areas.has(row.Area)) {
        areas.set(row.Area, {
          division: row.Division,
          area: row.Area,
          collectionQtyPercent: parseFloat(row.Collection_Qty_Percent),
          collectionAmtPercent: parseFloat(row.Collection_Amt_Percent),
          overdueChange: parseFloat(row.Overdue_Change),
        });
      }
    });

    // Get area subtotals from subtotal rows
    const areaSubtotals = new Map();
    areaWiseData.forEach(row => {
      if (row.isSubtotal && row.Area && row.Area.includes('SUBTOTAL') && !row.Area.includes('Division')) {
        const areaName = row.Area.replace(' - SUBTOTAL', '');
        areaSubtotals.set(areaName, {
          area: areaName,
          collectionQtyPercent: parseFloat(row.Collection_Qty_Percent),
          collectionAmtPercent: parseFloat(row.Collection_Amt_Percent),
          overdueChange: parseFloat(row.Overdue_Change),
        });
      }
    });

    // Sort and get top/bottom items
    const lowestQtyPlazas = plazas
      .sort((a, b) => a.collectionQtyPercent - b.collectionQtyPercent)
      .slice(0, 20);

    const lowestQtyAreas = Array.from(areaSubtotals.values())
      .sort((a, b) => a.collectionQtyPercent - b.collectionQtyPercent)
      .slice(0, 10);

    const lowestAmtPlazas = plazas
      .sort((a, b) => a.collectionAmtPercent - b.collectionAmtPercent)
      .slice(0, 20);

    const lowestAmtAreas = Array.from(areaSubtotals.values())
      .sort((a, b) => a.collectionAmtPercent - b.collectionAmtPercent)
      .slice(0, 10);

    const highestOverduePlazas = plazas
      .sort((a, b) => b.overdueChange - a.overdueChange)
      .slice(0, 20);

    const highestOverdueAreas = Array.from(areaSubtotals.values())
      .sort((a, b) => b.overdueChange - a.overdueChange)
      .slice(0, 10);

    return {
      lowestQtyPlazas,
      lowestQtyAreas,
      lowestAmtPlazas,
      lowestAmtAreas,
      highestOverduePlazas,
      highestOverdueAreas,
    };
  }, [areaWiseData]);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const handleDownloadExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: 20 Lowest Collection Qty % - Plazas
    const lowestQtyPlazasData = analytics.lowestQtyPlazas.map(item => ({
      'Plaza Name': item.plaza,
      'Area': item.area,
      'Division': item.division,
      'Collection Qty %': item.collectionQtyPercent.toFixed(2) + '%',
    }));
    const ws1 = XLSX.utils.json_to_sheet(lowestQtyPlazasData);
    XLSX.utils.book_append_sheet(workbook, ws1, 'Lowest Qty % Plazas');

    // Sheet 2: 10 Lowest Collection Qty % - Areas
    const lowestQtyAreasData = analytics.lowestQtyAreas.map(item => ({
      'Area Name': item.area,
      'Collection Qty %': item.collectionQtyPercent.toFixed(2) + '%',
    }));
    const ws2 = XLSX.utils.json_to_sheet(lowestQtyAreasData);
    XLSX.utils.book_append_sheet(workbook, ws2, 'Lowest Qty % Areas');

    // Sheet 3: 20 Lowest Collection Amt % - Plazas
    const lowestAmtPlazasData = analytics.lowestAmtPlazas.map(item => ({
      'Plaza Name': item.plaza,
      'Area': item.area,
      'Division': item.division,
      'Collection Amt %': item.collectionAmtPercent.toFixed(2) + '%',
    }));
    const ws3 = XLSX.utils.json_to_sheet(lowestAmtPlazasData);
    XLSX.utils.book_append_sheet(workbook, ws3, 'Lowest Amt % Plazas');

    // Sheet 4: 10 Lowest Collection Amt % - Areas
    const lowestAmtAreasData = analytics.lowestAmtAreas.map(item => ({
      'Area Name': item.area,
      'Collection Amt %': item.collectionAmtPercent.toFixed(2) + '%',
    }));
    const ws4 = XLSX.utils.json_to_sheet(lowestAmtAreasData);
    XLSX.utils.book_append_sheet(workbook, ws4, 'Lowest Amt % Areas');

    // Sheet 5: 20 Highest Overdue Change - Plazas
    const highestOverduePlazasData = analytics.highestOverduePlazas.map(item => ({
      'Plaza Name': item.plaza,
      'Area': item.area,
      'Division': item.division,
      'Overdue Change': item.overdueChange.toFixed(2),
    }));
    const ws5 = XLSX.utils.json_to_sheet(highestOverduePlazasData);
    XLSX.utils.book_append_sheet(workbook, ws5, 'Highest Overdue Plazas');

    // Sheet 6: 10 Highest Overdue Change - Areas
    const highestOverdueAreasData = analytics.highestOverdueAreas.map(item => ({
      'Area Name': item.area,
      'Overdue Change': item.overdueChange.toFixed(2),
    }));
    const ws6 = XLSX.utils.json_to_sheet(highestOverdueAreasData);
    XLSX.utils.book_append_sheet(workbook, ws6, 'Highest Overdue Areas');

    // Download the file
    XLSX.writeFile(workbook, 'performance_analytics.xlsx');
  };

  const renderTable = (data, columns) => (
    <div className="analytics-table">
      <table>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, idx) => (
            <tr key={idx}>
              {columns.map(col => (
                <td key={col.key} className={col.className || ''}>
                  {col.format ? col.format(item[col.key]) : item[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="analytics-section">
      <div className="analytics-container">
        <div className="analytics-title-row">
          <h3>📈 Performance Analytics</h3>
          <button className="download-analytics-btn" onClick={handleDownloadExcel}>
            ⬇ Download Excel
          </button>
        </div>

        {/* Lowest Collection Qty Percentage */}
        <div className="analytics-card">
          <div 
            className="analytics-header"
            onClick={() => toggleSection('lowestQty')}
          >
            <h4>📉 20 Lowest Collection Qty % - Plazas</h4>
            <span className="toggle-icon">{expandedSections['lowestQty'] ? '▼' : '▶'}</span>
          </div>
          {expandedSections['lowestQty'] && (
            <div className="analytics-content">
              {renderTable(analytics.lowestQtyPlazas, [
                { key: 'plaza', label: 'Plaza Name' },
                { key: 'area', label: 'Area' },
                { key: 'division', label: 'Division' },
                { key: 'collectionQtyPercent', label: 'Collection Qty %', format: (v) => v.toFixed(2) + '%', className: 'text-right' },
              ])}
            </div>
          )}
        </div>

        <div className="analytics-card">
          <div 
            className="analytics-header"
            onClick={() => toggleSection('lowestQtyArea')}
          >
            <h4>📉 10 Lowest Collection Qty % - Areas</h4>
            <span className="toggle-icon">{expandedSections['lowestQtyArea'] ? '▼' : '▶'}</span>
          </div>
          {expandedSections['lowestQtyArea'] && (
            <div className="analytics-content">
              {renderTable(analytics.lowestQtyAreas, [
                { key: 'area', label: 'Area Name' },
                { key: 'collectionQtyPercent', label: 'Collection Qty %', format: (v) => v.toFixed(2) + '%', className: 'text-right' },
              ])}
            </div>
          )}
        </div>

        {/* Lowest Collection Amt Percentage */}
        <div className="analytics-card">
          <div 
            className="analytics-header"
            onClick={() => toggleSection('lowestAmt')}
          >
            <h4>📉 20 Lowest Collection Amt % - Plazas</h4>
            <span className="toggle-icon">{expandedSections['lowestAmt'] ? '▼' : '▶'}</span>
          </div>
          {expandedSections['lowestAmt'] && (
            <div className="analytics-content">
              {renderTable(analytics.lowestAmtPlazas, [
                { key: 'plaza', label: 'Plaza Name' },
                { key: 'area', label: 'Area' },
                { key: 'division', label: 'Division' },
                { key: 'collectionAmtPercent', label: 'Collection Amt %', format: (v) => v.toFixed(2) + '%', className: 'text-right' },
              ])}
            </div>
          )}
        </div>

        <div className="analytics-card">
          <div 
            className="analytics-header"
            onClick={() => toggleSection('lowestAmtArea')}
          >
            <h4>📉 10 Lowest Collection Amt % - Areas</h4>
            <span className="toggle-icon">{expandedSections['lowestAmtArea'] ? '▼' : '▶'}</span>
          </div>
          {expandedSections['lowestAmtArea'] && (
            <div className="analytics-content">
              {renderTable(analytics.lowestAmtAreas, [
                { key: 'area', label: 'Area Name' },
                { key: 'collectionAmtPercent', label: 'Collection Amt %', format: (v) => v.toFixed(2) + '%', className: 'text-right' },
              ])}
            </div>
          )}
        </div>

        {/* Highest Overdue Change */}
        <div className="analytics-card">
          <div 
            className="analytics-header"
            onClick={() => toggleSection('highestOverdue')}
          >
            <h4>📈 20 Highest Overdue Change Amount - Plazas</h4>
            <span className="toggle-icon">{expandedSections['highestOverdue'] ? '▼' : '▶'}</span>
          </div>
          {expandedSections['highestOverdue'] && (
            <div className="analytics-content">
              {renderTable(analytics.highestOverduePlazas, [
                { key: 'plaza', label: 'Plaza Name' },
                { key: 'area', label: 'Area' },
                { key: 'division', label: 'Division' },
                { key: 'overdueChange', label: 'Overdue Change', format: (v) => v.toFixed(2), className: 'text-right highlight-red' },
              ])}
            </div>
          )}
        </div>

        <div className="analytics-card">
          <div 
            className="analytics-header"
            onClick={() => toggleSection('highestOverdueArea')}
          >
            <h4>📈 10 Highest Overdue Change Amount - Areas</h4>
            <span className="toggle-icon">{expandedSections['highestOverdueArea'] ? '▼' : '▶'}</span>
          </div>
          {expandedSections['highestOverdueArea'] && (
            <div className="analytics-content">
              {renderTable(analytics.highestOverdueAreas, [
                { key: 'area', label: 'Area Name' },
                { key: 'overdueChange', label: 'Overdue Change', format: (v) => v.toFixed(2), className: 'text-right highlight-red' },
              ])}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default AnalyticsSection;
